import * as React from 'react'

import {
  ProfileEmployeeAccessFragment,
  ProfileEmployeePoliciesFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { EmployeeStatusBannerEmployee } from 'modules/employees/components/blocks/EmployeeStatusBanner/types'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'

import InvitationAccepted from './InvitationAccepted'
import InvitationAcceptedWithoutOnboarding from './InvitationAcceptedWithoutOnboarding'
import InvitationNotSentOrAccepted from './InvitationNotSentOrAccepted'

interface Props {
  access: ProfileEmployeeAccessFragment
  contractStartDate?: string | null
  employee: EmployeeStatusBannerEmployee
  policies?: ProfileEmployeePoliciesFragment | null
  onboardingProcessName?: string
}

const EmployeeStatusBanner = ({
  access,
  contractStartDate,
  employee,
  policies,
  onboardingProcessName,
}: Props) => {
  const [onboardingCreated, setOnboardingCreated] = React.useState<string | undefined>(undefined)
  const { canCreateEmployees, employee: currentEmployee } = useCurrentSession()

  const invitationSentAndAccepted = Boolean(access.invitedOn && access.isConfirmed)

  if (
    invitationSentAndAccepted &&
    contractStartDate &&
    moment(contractStartDate).isSameOrBefore(moment(), 'day')
  ) {
    return null
  }

  if (invitationSentAndAccepted) {
    if (!canCreateEmployees || employee.isTerminating) return null
    if (!currentEmployee?.hasRegularAccess) return null
    if (!hasRegularAccess(employee.hasRegularAccess)) return null

    const process = onboardingProcessName || onboardingCreated
    if (process) {
      return <InvitationAccepted employee={employee} onboardingProcessName={process} />
    }

    return (
      <InvitationAcceptedWithoutOnboarding
        access={access}
        employee={employee}
        contractStartDate={contractStartDate || undefined}
        setOnboardingProcessCreated={setOnboardingCreated}
      />
    )
  }

  return <InvitationNotSentOrAccepted employee={employee} access={access} policies={policies} />
}

export default observer(EmployeeStatusBanner)
